export type SaunaSlug = "advise-design" | "supply" | "install" | "maintenance-repair";

export interface SaunaService {
  slug: SaunaSlug;
  href: string;
  navTitle: string;
  navDesc: string;
  index: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  headline: string;
  headlineAccent: string;
  intro: string;
  ctaLabel: string;
  focus: { title: string; body: string }[];
  steps: { title: string; body: string }[];
  prepare: string[];
  note: { title: string; body: string };
  audiences: { who: string; body: string }[];
}

export const saunaServices: SaunaService[] = [
  {
    slug: "advise-design",
    href: "/sauna/advise-design",
    navTitle: "Advise & Design",
    navDesc: "Planning, site assessment and a heater, ventilation and power plan.",
    index: "01",
    metaTitle: "Sauna Advice & Design | Planning & Site Assessment | VertexAir",
    metaDescription:
      "Sauna planning and design advice from VertexAir. Site assessment, heater sizing, ventilation and electrical considerations for homes, businesses and trade projects across the UK.",
    eyebrow: "Sauna / Advise & Design",
    headline: "Plan the heat",
    headlineAccent: "before the timber.",
    intro:
      "A sauna is a small room with big demands: concentrated heat, moisture, airflow and a dedicated electrical supply. We help you work through those requirements early, so the room you build is the room that performs.",
    ctaLabel: "Request a design consultation",
    focus: [
      { title: "Site assessment", body: "We look at the proposed space, its construction, access, the route for power and where fresh air can come in and go out." },
      { title: "Heater sizing", body: "Heater output is matched to the room volume, insulation and any glazing or masonry that will absorb heat." },
      { title: "Ventilation plan", body: "Inlet and outlet positions are planned so the room heats evenly and air changes comfortably. This is our everyday discipline as climate engineers." },
      { title: "Electrical considerations", body: "Our fully qualified electricians assess the heater's supply requirements, consumer unit and circuits, and can include the necessary electrical work in your installation quotation." },
    ],
    steps: [
      { title: "Initial conversation", body: "Tell us about the space, how you intend to use it and any constraints you already know about." },
      { title: "Site visit or survey", body: "We review dimensions, construction, ventilation routes and power availability on site where needed." },
      { title: "Design outline", body: "You receive a written outline covering room layout, heater type, ventilation and controls, with options explained plainly." },
      { title: "Next steps", body: "Take the plan forward with us for supply and installation, or use it to brief your own builder." },
    ],
    prepare: [
      "Approximate room dimensions and ceiling height",
      "Whether the space is indoors, a garden building or a commercial room",
      "Photos of the area and nearby consumer unit, if available",
      "How many people will use it and how often",
    ],
    note: {
      title: "Advice that stays independent of the hardware",
      body: "A design outline is useful whether or not we go on to supply equipment. We will tell you where a requirement sits outside our scope, such as structural or building control questions.",
    },
    audiences: [
      { who: "Homeowners", body: "Converting a spare room, loft or garden building into a home sauna." },
      { who: "Businesses", body: "Gyms, spas, hotels and wellness spaces planning a new or replacement room." },
      { who: "Trade", body: "Builders and designers who need a technical partner for heat and ventilation." },
    ],
  },
  {
    slug: "supply",
    href: "/sauna/supply",
    navTitle: "Supply",
    navDesc: "Heaters, controls, ventilation components and replacement parts.",
    index: "02",
    metaTitle: "Sauna Equipment & Parts Supply | Heaters & Controls | VertexAir",
    metaDescription:
      "Sauna heaters, controls, ventilation components and replacement parts sourced by VertexAir for homeowners, businesses and trade. Supply subject to manufacturer availability.",
    eyebrow: "Sauna / Supply",
    headline: "The right parts,",
    headlineAccent: "specified properly.",
    intro:
      "We source sauna heaters, control panels, sensors, ventilation components and replacement parts, and we check compatibility before anything is ordered. Availability and lead times depend on the manufacturer, and we confirm both with you upfront.",
    ctaLabel: "Ask about equipment",
    focus: [
      { title: "Heaters", body: "Electric sauna heaters selected for room volume and supply capacity, alongside any compatible stones and guards." },
      { title: "Controls & sensors", body: "Control panels, temperature sensors and timers, matched to the heater they will operate." },
      { title: "Ventilation components", body: "Vents, valves and fans suitable for the temperatures and humidity a sauna room produces." },
      { title: "Replacement parts", body: "Elements, sensors and components for existing installations, identified from model details where possible." },
    ],
    steps: [
      { title: "Share the details", body: "Send the room size, existing equipment model numbers or your design outline." },
      { title: "Compatibility check", body: "We confirm the part or system suits your room, supply and controls before quoting." },
      { title: "Availability confirmed", body: "We check stock and lead times with suppliers and give you a realistic timeframe." },
      { title: "Collection or fitting", body: "Arrange delivery for your installer, or have our engineers fit it for you." },
    ],
    prepare: [
      "Make and model of the existing heater or controller",
      "Photos of the rating plate or label",
      "Room dimensions for new equipment",
      "Whether you need fitting as well as supply",
    ],
    note: {
      title: "Honest lead times",
      body: "Sauna equipment is often made to order or held by specialist distributors. We will not promise a delivery date until the supplier has confirmed it.",
    },
    audiences: [
      { who: "Homeowners", body: "Replacing a failed heater or controller in an existing sauna." },
      { who: "Businesses", body: "Sourcing spares to reduce downtime for a busy facility." },
      { who: "Trade", body: "Contractors who need specified equipment for a project they are building." },
    ],
  },
  {
    slug: "install",
    href: "/sauna/install",
    navTitle: "Install",
    navDesc: "Heater, controls and ventilation fitted, then commissioned and handed over.",
    index: "03",
    metaTitle: "Sauna Installation & Commissioning | VertexAir UK",
    metaDescription:
      "Sauna heater, control and ventilation installation with commissioning and handover by VertexAir engineers. For homeowners, businesses and trade partners across the UK.",
    eyebrow: "Sauna / Install",
    headline: "Fitted, tested,",
    headlineAccent: "then handed over.",
    intro:
      "Our engineers install sauna heaters, controls and ventilation, then commission the system: bringing the room up to temperature, checking controls and safety cut-outs behave as expected, and showing you how to use it.",
    ctaLabel: "Get an installation quote",
    focus: [
      { title: "Heater & guard", body: "Heater mounted to the manufacturer's clearances with its guard and stones correctly placed." },
      { title: "Controls & sensors", body: "Control panel and sensor positioned and wired to the heater as specified." },
      { title: "Ventilation", body: "Inlet and outlet vents installed to the agreed plan so air moves through the room as intended." },
      { title: "Commissioning", body: "First heat-up, control checks and a walkthrough of operation and basic care." },
    ],
    steps: [
      { title: "Survey & quote", body: "We confirm the room, equipment and power arrangements and provide a written quote." },
      { title: "Schedule", body: "Installation is booked once equipment availability and site readiness are confirmed." },
      { title: "Installation", body: "Heater, controls and ventilation are fitted, with electrical work carried out by our fully qualified electricians as part of the agreed installation." },
      { title: "Commission & handover", body: "We run the system, check it operates correctly and explain use and maintenance." },
    ],
    prepare: [
      "The room or cabin is built and ready to receive equipment",
      "A suitable electrical supply is in place or arranged",
      "Clear access to the room and the consumer unit",
      "Equipment details if supplied by others",
    ],
    note: {
      title: "Timeframes set by the job, not a slogan",
      body: "Installation dates depend on equipment lead times and site readiness. We agree a date with you once both are confirmed.",
    },
    audiences: [
      { who: "Homeowners", body: "A complete fit in a new room, cabin or garden building." },
      { who: "Businesses", body: "Installations scheduled around opening hours where possible." },
      { who: "Trade", body: "Fitting and commissioning on projects where you handle the build." },
    ],
  },
  {
    slug: "maintenance-repair",
    href: "/sauna/maintenance-repair",
    navTitle: "Maintenance & Repair",
    navDesc: "Servicing, diagnostics and repairs for heaters, controls and ventilation.",
    index: "04",
    metaTitle: "Sauna Maintenance & Repair | Servicing & Diagnostics | VertexAir",
    metaDescription:
      "Sauna servicing, fault diagnostics and repairs for heaters, controls and ventilation from VertexAir. Support for homeowners, commercial facilities and trade across the UK.",
    eyebrow: "Sauna / Maintenance & Repair",
    headline: "Keep it heating",
    headlineAccent: "the way it should.",
    intro:
      "Heaters lose elements, sensors drift and vents get blocked. We service sauna systems on a planned basis and diagnose faults when something stops working, then repair or replace parts as needed.",
    ctaLabel: "Book a service or repair",
    focus: [
      { title: "Routine servicing", body: "Visual and functional checks of the heater, stones, controls, sensors and ventilation." },
      { title: "Fault diagnostics", body: "Systematic testing to find why a room will not heat, heats slowly or cuts out." },
      { title: "Repairs", body: "Replacement of elements, sensors and components, subject to parts availability." },
      { title: "Planned maintenance", body: "Scheduled visits for commercial facilities, using the same approach as our HVAC maintenance." },
    ],
    steps: [
      { title: "Describe the issue", body: "Tell us the symptoms and share the equipment make and model." },
      { title: "Diagnostic visit", body: "An engineer tests the system and identifies the cause." },
      { title: "Repair options", body: "We explain what is needed and confirm parts availability before going ahead." },
      { title: "Return to service", body: "Once repaired, the system is tested through a heat-up cycle." },
    ],
    prepare: [
      "Make and model of the heater and controller",
      "What happens when the fault occurs, and any error codes",
      "Age of the installation, if known",
      "Previous service or repair records",
    ],
    note: {
      title: "Fair expectations on repairs",
      body: "Some faults can be resolved on the first visit. Others need parts ordered, and we will be clear about which applies once we have diagnosed the system.",
    },
    audiences: [
      { who: "Homeowners", body: "Servicing or a fault visit for a home sauna that has stopped performing." },
      { who: "Businesses", body: "Planned maintenance to keep facility saunas available for members and guests." },
      { who: "Trade", body: "Diagnostic support for installers and facilities managers." },
    ],
  },
];

export const getSaunaService = (slug: SaunaSlug) => saunaServices.find((s) => s.slug === slug)!;
